// package imports
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

// CSS imports
import './NewsletterEmails.css';

// Component imports
import Spinner from '../../components/spinner/Spinner';
import Alert from '../../components/alert/Alert';
import PrimaryButton from '../../components/buttons/primaryButton/PrimaryButton';
import SearchBar from '../../components/searchBar/SearchBar';
import NewsletterEmailCard from '../../components/newsletterEmailCard/NewsletterEmailCard';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';
import { FaPlus } from 'react-icons/fa6';
import { FaCheck } from 'react-icons/fa6';

// Type imports
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { AlertSize } from '../../types/alert/AlertSize';
import { NewsletterEmail } from '../../types/newsletter/NewsletterEmail';
import { NewsletterEmailsResult } from '../../types/newsletter/NewsletterEmailsResult';
import { AlertColor } from '../../types/alert/AlertColor';

const NewsletterEmails: React.FC = () => {
  const [search, setSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [emails, setEmails] = useState<NewsletterEmail[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [showUnknownError, setShowUnknownError] = useState(false);
  const [showSendSuccess, setShowSendSuccess] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const getNewsletterEmails = useCallback(
    async (tempEmails: NewsletterEmail[], search: string, page: number) => {
      setIsLoading(true);
      const res: NewsletterEmailsResult | null =
        await ApiService.getNewsletterEmails(search, page, 9);
      setIsLoading(false);
      if (res) {
        for (const email of res.emails) {
          tempEmails.push(email);
        }
        setEmails(tempEmails);
        setSelectedPage(res.page);
        setPagesCount(res.pages);
      }
    },
    []
  );

  useEffect(() => {
    let urlSearch = searchParams.get('search');
    let finalSearch = urlSearch ? decodeURI(urlSearch) : '';

    setSelectedSearch(finalSearch);

    getNewsletterEmails([], finalSearch, 1);
  }, [searchParams, getNewsletterEmails]);

  const deleteEmail = async (id: string, index: number) => {
    const res: boolean = await ApiService.deleteNewsletterEmail(id);
    if (res) {
      const tempEmails = emails.slice(0, emails.length);
      tempEmails.splice(index, 1);
      setEmails(tempEmails);
    } else {
      setShowUnknownError(true);
    }
  };

  const duplicateEmail = async (id: string) => {
    const res: NewsletterEmail | null =
      await ApiService.duplicateNewsletterEmail(id);
    if (res) {
      const tempEmails = emails.slice(0, emails.length);
      tempEmails.unshift(res);
      setEmails(tempEmails);
    } else {
      setShowUnknownError(true);
    }
  };

  const sendEmail = async (id: string) => {
    const res: boolean = await ApiService.sendNewsletterEmail(id);
    if (res) {
      setShowSendSuccess(true);
    } else {
      setShowUnknownError(true);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const changeSearch = async (newSearch: string) => {
    navigate(
      '/newsletter-emails' +
        (newSearch !== '' ? '?search=' + encodeURI(newSearch) : '')
    );
  };

  return (
    <div className='NewsletterEmails'>
      <Link to='/newsletter-email/create' className='add-button'>
        <PrimaryButton ReactIcon={FaPlus} />
      </Link>
      <SectionWrapper contentWidth={SectionContentSize.MEDIUM}>
        <div className='content'>
          <h1>Newsletter Emails</h1>
          <SearchBar
            className='search-bar'
            value={search}
            placeholder='Search'
            onChange={onSearchChange}
            triggerSearch={changeSearch}
          />
          <InfiniteScroll
            dataLength={emails.length}
            hasMore={selectedPage !== pagesCount}
            next={() =>
              getNewsletterEmails(emails, selectedSearch, selectedPage + 1)
            }
            loader={<Spinner isLoading={true} />}
            className='infinite-scroll-container'
          >
            {emails.map((email: NewsletterEmail, index: number) => (
              <NewsletterEmailCard
                className='item-card'
                index={index}
                email={email}
                deleteEmail={deleteEmail}
                duplicateEmail={duplicateEmail}
                sendEmail={sendEmail}
                key={index}
              />
            ))}
          </InfiniteScroll>
          {!isLoading && emails.length === 0 ? (
            <span className='no-items-found'>No emails found</span>
          ) : null}
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
      <Alert
        showAlert={showSendSuccess}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowSendSuccess}
        color={AlertColor.GREEN}
      >
        <FaCheck size={20} />
        <span>The emails are being sent!</span>
      </Alert>
    </div>
  );
};

export default NewsletterEmails;
